import React from "react";
import i18n, { useTranslation } from "utilities/i18n";
import {
  convertToBase64,
  createDownloadableFile,
  parseCSV
} from "utilities/fileUtils";
import { array, boolean, mixed, object } from "yup";
import {
  CSV_MANDATORY_HEADERS,
  DEVICE_IMPORT_CSV_TEMPLATE_TEXT,
  ZIP_SUPPORTED_FORMATS,
  CSV_DEVICE_GROUPS_HEADER,
  CSV_SITE_NAME_HEADER,
  FILE_EXTENSIONS,
  DOCS,
  CSV_ROOM_NAME_HEADER,
  CSV_MAX_FILE_SIZE_MB,
  CSV_MAX_FILE_SIZE,
  CSV_SUPPORTED_FORMATS
} from "Constants";
import { protect } from "TsRoleGuards";
import { Import, ImportType } from "./Import";
import { HelpLink } from "elements";

export const fileUploadSchema = object({
  files: array().of(
    mixed()
      .test(
        "fileSize",
        i18n.t("FILE_SIZE_ERROR_DESC", { size: CSV_MAX_FILE_SIZE_MB }),
        value => value.size < CSV_MAX_FILE_SIZE
      )
      .test(
        "fileFormat",
        i18n.t("FILE_FORMAT_ERROR"),
        value =>
          [...ZIP_SUPPORTED_FORMATS, ...CSV_SUPPORTED_FORMATS].includes(
            value.type
          ) && ["csv", "zip"].includes(value.name.split(".").pop())
      )
  )
});

export const devicesImportSchema = fileUploadSchema.shape({
  updateExistingDevices: boolean().required(),
  overwriteDeviceRecords: boolean().required()
});

export const prepareFileForImport = async (
  file: File,
  type: ImportType,
  allowedEmptyHeaders: string[] = []
) => {
  let importData = "";
  if (ZIP_SUPPORTED_FORMATS.includes(file.type)) {
    const base64 = (await convertToBase64(file)) as string;
    importData = base64.split(",").pop() as string;
  } else {
    const parsedData = await parseCSV(file);
    importData = JSON.stringify(
      cleanCSVData(parsedData, type, allowedEmptyHeaders)
    );
  }
  return importData;
};

// Ensures each row has mandatory headers; strips empty properties apart from those specified as allowed to be empty
export const cleanCSVData = (
  data: object[],
  type: ImportType,
  allowedEmptyHeaders: string[] = []
) => {
  return data
    .map(row => removeEmptyProperties(row, allowedEmptyHeaders))
    .filter(row => {
      const columns = Object.keys(row);
      return CSV_MANDATORY_HEADERS[type].every(header =>
        columns.includes(header)
      );
    });
};

const removeEmptyProperties = (
  data: object,
  allowedEmptyHeaders: string[] = []
) => {
  return Object.fromEntries(
    Object.entries(data)
      .filter(
        ([header, value]) =>
          allowedEmptyHeaders.includes(header) ||
          (value != null && value !== "")
      )
      .map(([header, value]) => [header, value ?? ""])
  );
};

const DevicesImport: React.FC = () => {
  const { t } = useTranslation();

  const getCSVRules = () => (
    <div className="mb-1">
      <p>{t("CSV_IMPORT_RULES_1_DEVICE")}</p>
      <p>
        {t("CSV_IMPORT_RULES_2")} {t("CSV_IMPORT_RULES_HEADERS")}
        <strong>{t("CSV_IMPORT_HEADER_MAC")}</strong>
        {", "}
        <strong>{t("CSV_IMPORT_HEADER_SERIAL")}</strong>
        {t("AND")}
        <strong>{t("CSV_IMPORT_HEADER_MODEL")}</strong>
        {t("CSV_IMPORT_RULES_3")}
      </p>
      <p>
        {t("CSV_IMPORT_RULES_4")}
        <HelpLink location={DOCS.CSV_IMPORT}>
          {t("CSV_IMPORT_RULES_5")}
        </HelpLink>
      </p>
      <p>
        {t("CSV_IMPORT_RULES_6", { entity: t("DEVICE_other").toLowerCase() })}
      </p>
    </div>
  );

  return (
    <Import
      importType="DEVICE"
      fileTypesAccepted={[FILE_EXTENSIONS.CSV, FILE_EXTENSIONS.ZIP]}
      validationSchema={fileUploadSchema}
      prepareFileForImport={(file, importType) =>
        prepareFileForImport(file, importType, [
          CSV_DEVICE_GROUPS_HEADER,
          CSV_SITE_NAME_HEADER,
          CSV_ROOM_NAME_HEADER
        ])
      }
      importRules={getCSVRules()}
      templateDownload={
        <TemplateDownloadButton
          template={DEVICE_IMPORT_CSV_TEMPLATE_TEXT}
          fileName="Devices-Template.csv"
        />
      }
    />
  );
};

export const TemplateDownloadButton: React.FC<{
  template: string;
  fileName: string;
}> = ({ template, fileName }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="btn btn-link font-bold mb-15"
      onClick={() => createDownloadableFile(template, fileName)}
    >
      {t("CSV_DOWNLOAD_TEMPLATE")}
    </button>
  );
};

export default protect("set:device:config")(DevicesImport);
